import type { FormikErrors } from 'formik';
import { Form, Formik } from 'formik';
import type { ReactElement } from 'react';
import { useState } from 'react';
import z, { ZodError } from 'zod';

import type { AccountPaymentsSuccessResponse } from '@libs/common-next';
import { BANNERS, logSentryError, SentryAcpCreditCardsErrors as SentryErrors } from '@libs/common-next';

import type { AddressFormTypeUSandCA } from '../AddressForm';
import { BillingAddress } from '../BillingAddress';
import type { ExperianResponseLayout } from '../ExperianModal';
import { ExperianModal, validateExperianAddressLayout } from '../ExperianModal';
import { CardDetailsInputs } from './CardDetailsInputs';
import { IsDefaultCheckbox } from './IsDefaultCheckbox';
import type { EditPaymentFormInterface } from './schema';
import { editPaymentFormSchema } from './schema';
import { formatCreditCardExpirationDate, updatePayment, validateAddressForm } from './utils';

const scrollToFirstErrorField = (errorKey: string) => {
    const field = document.querySelector<HTMLElement>(`[name="${errorKey}"]`);
    if (field && typeof field.scrollIntoView === 'function') {
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    field?.focus();
};

const DEFAULT_EDIT_PAYMENT_FORM_STATE: EditPaymentFormInterface = {
    addressType: 'id',
    nameOnCard: '',
    expirationDate: '',
    isPrimary: false,
    selectedAddressId: '',
    cardId: 0,
};

export function EditPaymentForm({
    isDefaultCheckDisabled,
    onCloseForm,
    onSuccess,
    onError,
    defaultAddressId,
    editPaymentData,
    onSubmit,
    onAddressValidationApiError,
    onExperianModalVisibilityChange = () => {},
    addAddressIconSize,
    onClearError,
}: Readonly<{
    isDefaultCheckDisabled: boolean;
    editPaymentData: AccountPaymentsSuccessResponse;
    onCloseForm: () => void;
    onSuccess: (isDefault: boolean) => Promise<void>;
    onError?: (error: unknown) => void;
    defaultAddressId?: string;
    onSubmit?: (values: EditPaymentFormInterface) => Promise<void>;
    onAddressValidationApiError?: () => void;
    onExperianModalVisibilityChange?: (isOpen: boolean) => void;
    disableUntilDirty?: boolean;
    addAddressIconSize?: 'sm' | 'md' | 'lg';
    onClearError?: () => void;
}>): ReactElement {
    const [isExperianLoading, setIsExperianLoading] = useState(false);
    const [experianResponse, setExperianResponse] = useState<ExperianResponseLayout | null>(null);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const paymentFormState: EditPaymentFormInterface = {
        ...DEFAULT_EDIT_PAYMENT_FORM_STATE,
        addressType: 'id',
        selectedAddressId: editPaymentData.addressId?.toString() ?? '',
        nameOnCard: editPaymentData.fullName,
        expirationDate: formatCreditCardExpirationDate(editPaymentData.expiration),
        isPrimary: editPaymentData.isPrimary,
        cardId: editPaymentData.cardId,
    };

    const validate = async (values: EditPaymentFormInterface) => {
        let errors: FormikErrors<EditPaymentFormInterface> = {};

        try {
            editPaymentFormSchema.parse(values);
        } catch (error) {
            if (error instanceof ZodError) {
                errors = z.flattenError(error).fieldErrors;
            }
        }

        if (values.address) {
            const addressErrors = await validateAddressForm(values.address);

            if (addressErrors) {
                errors.address = addressErrors as FormikErrors<AddressFormTypeUSandCA>;
            }
        }

        return Object.keys(errors) ? errors : true;
    };

    const handleErrorBanner = (errors?: FormikErrors<EditPaymentFormInterface>): boolean => {
        const hasFormErrors = errors ? Object.keys(errors).length > 0 : false;

        if (hasFormErrors && errors) {
            const isMissingAddressOnly = Object.keys(errors).length === 1 && 'selectedAddressId' in errors;
            const errorMessage = isMissingAddressOnly
                ? 'Please select or add a billing address'
                : (BANNERS.invalidPaymentMethodFormik.text ?? '');

            onError?.(errorMessage);
            scrollToFirstErrorField(Object.keys(errors)[0]);
            return false;
        }
        onClearError?.();

        return true;
    };

    const handleSubmitForm = async (values: EditPaymentFormInterface): Promise<void> => {
        onClearError?.();

        setIsSubmitting(true);

        if (onSubmit) {
            await onSubmit(values);
            setIsSubmitting(false);
            return;
        }

        try {
            const response = await updatePayment(values);

            if (response && 'cardId' in response) {
                await onSuccess?.(response.isPrimary);
            } else {
                throw new Error('Failed to update payment method');
            }
        } catch (error) {
            logSentryError(error, SentryErrors.EditPaymentForm.SubmitFailed);

            onError?.(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const save = async (values: EditPaymentFormInterface) => {
        try {
            await handleSubmitForm(values);
        } finally {
            setIsSubmitting(false);
        }
    };

    const validateExperianOnSubmit = async (values: EditPaymentFormInterface) => {
        onClearError?.();

        if (values.addressType === 'id') {
            await save(values);
        } else {
            const addressValues = values?.address;
            setIsExperianLoading(true);
            const experianResponseValue = await validateExperianAddressLayout(addressValues);
            setIsExperianLoading(false);

            if (!experianResponseValue) {
                onAddressValidationApiError?.();
                return;
            }

            setExperianResponse(experianResponseValue);
        }
    };

    return (
        <div>
            <Formik initialValues={paymentFormState} validate={validate} onSubmit={validateExperianOnSubmit}>
                {formikProps => (
                    <Form noValidate onSubmit={formikProps.handleSubmit} data-testid="billing-address-form">
                        <CardDetailsInputs cardData={editPaymentData} />

                        <ExperianModal
                            experianResponse={experianResponse}
                            saveSelectedAddress={async (formProps: unknown) => {
                                await save(formProps as EditPaymentFormInterface);
                            }}
                            loading={isExperianLoading}
                            setIsLoading={setIsExperianLoading}
                            onVisibilityChange={onExperianModalVisibilityChange}
                        />
                        <IsDefaultCheckbox disabled={isDefaultCheckDisabled} />
                        <BillingAddress
                            onCancel={onCloseForm}
                            defaultAddressId={defaultAddressId}
                            isLoading={isExperianLoading || isSubmitting}
                            handleErrorBanner={handleErrorBanner}
                            addAddressIconSize={addAddressIconSize}
                        />
                    </Form>
                )}
            </Formik>
        </div>
    );
}
